import { useMemo } from 'react'
import { Box, Typography, Paper, Stack, Chip, Alert } from '@mui/material'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts'

interface ForecastDataPoint {
  date: string
  label: string
  actual?: number
  forecast: number
  upperBound?: number
  lowerBound?: number
}

interface PredictiveChartProps {
  data: ForecastDataPoint[]
  title: string
  metric: 'deposits' | 'withdrawals' | 'net' | 'volume'
  showConfidenceInterval?: boolean
  forecastAccuracy?: number
}

const PredictiveChart = ({
  data,
  title,
  metric,
  showConfidenceInterval = true,
  forecastAccuracy,
}: PredictiveChartProps) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatValue = (value: number) => {
    if (metric === 'deposits' || metric === 'withdrawals' || metric === 'net') {
      return formatCurrency(value)
    }
    return value.toLocaleString()
  }

  const chartData = useMemo(() => {
    return data.map((point) => ({
      ...point,
      // Separate historical (actual) and forecast data
      isForecast: point.actual === undefined,
    }))
  }, [data])

  const historicalData = chartData.filter((d) => !d.isForecast)
  const forecastData = chartData.filter((d) => d.isForecast)

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{title}</Typography>
        {forecastAccuracy !== undefined && (
          <Chip
            label={`Accuracy: ${forecastAccuracy.toFixed(1)}%`}
            color={forecastAccuracy > 90 ? 'success' : forecastAccuracy > 75 ? 'warning' : 'error'}
            size="small"
          />
        )}
      </Stack>

      {forecastAccuracy !== undefined && forecastAccuracy < 75 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Forecast accuracy is below 75%. Consider reviewing the model parameters.
        </Alert>
      )}

      <Box sx={{ height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          {showConfidenceInterval && forecastData.some((d) => d.upperBound && d.lowerBound) ? (
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis tickFormatter={(value) => formatValue(value)} />
              <Tooltip formatter={(value: number) => formatValue(value)} />
              <Legend />
              {historicalData.length > 0 && (
                <Area
                  type="monotone"
                  dataKey="actual"
                  stroke="#1976d2"
                  fill="#1976d2"
                  fillOpacity={0.3}
                  name="Actual"
                  strokeWidth={2}
                />
              )}
              <Area
                type="monotone"
                dataKey="forecast"
                stroke="#ff9800"
                fill="#ff9800"
                fillOpacity={0.2}
                name="Forecast"
                strokeWidth={2}
                strokeDasharray="5 5"
              />
              {showConfidenceInterval && (
                <>
                  <Area
                    type="monotone"
                    dataKey="upperBound"
                    stroke="none"
                    fill="#ff9800"
                    fillOpacity={0.1}
                    name="Upper Bound"
                  />
                  <Area
                    type="monotone"
                    dataKey="lowerBound"
                    stroke="none"
                    fill="#ff9800"
                    fillOpacity={0.1}
                    name="Lower Bound"
                  />
                </>
              )}
            </AreaChart>
          ) : (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis tickFormatter={(value) => formatValue(value)} />
              <Tooltip formatter={(value: number) => formatValue(value)} />
              <Legend />
              {historicalData.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="#1976d2"
                  strokeWidth={2}
                  name="Actual"
                  dot={{ r: 4 }}
                />
              )}
              <Line
                type="monotone"
                dataKey="forecast"
                stroke="#ff9800"
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Forecast"
                dot={{ r: 4 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
        <Chip label={`Historical: ${historicalData.length} points`} size="small" variant="outlined" />
        <Chip label={`Forecast: ${forecastData.length} points`} size="small" variant="outlined" />
        {showConfidenceInterval && (
          <Chip label="95% Confidence Interval" size="small" variant="outlined" />
        )}
      </Stack>
    </Paper>
  )
}

export default PredictiveChart


